import React, { useState, useEffect, useCallback } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { NotificationManager } from "react-notifications";
import IntlMessages, { IntlGetText } from "util/IntlMessages";
import { useIntl } from "react-intl";
import FormSelectAutoComplete from "../../../../../components/Form/Controls/select-autocomplete";
import FormInput from "../../../../../components/Form/Controls/input";
import ButtonAsync from "components/Form/Controls/ButtonAsync";
import Table, { getTextLabels, defaultCol } from "components/Form/Table";
import {
  callApi,
  callApiAsync,
  getInsuranceList,
  getInstitutionList,
  getDoctorList,
  getProductList,
  postNewPatientAlert,
  postSearchPatients,
  getPrescriptions,
} from "../../../../../api/apiList";
import { setSelectedPrescription, loadPrescriptions } from "actions/Management";
import { setLoader } from "actions/Base";
import { getUserInfo } from "app/routes/psp/services/userService";
import { ShardKeyEnum } from "constants/Enums";

const defaultValues = {
  cboInsurance: "",
  cboInstitution: "",
  cboDoctor: "",
  cboProduct: "",
  searchDocument: "",
  searchName: "",
  searchLastName: "",
  searchPhone: "",
};

const initialSearchState = {
  hasSearched: false,
  results: [],
  totalCount: 0,
  page: 0,
  rowsPerPage: 10,
};

const buildSearchPayload = (data, page, rowsPerPage) => {
  const payload = { page, rowsPerPage };
  const doc = data.searchDocument && data.searchDocument.trim();
  const name = data.searchName && data.searchName.trim();
  const lastName = data.searchLastName && data.searchLastName.trim();
  const phone = data.searchPhone && data.searchPhone.trim();

  if (doc) payload.document = doc;
  if (name) payload.name = name;
  if (lastName) payload.lastName = lastName;
  if (phone) payload.phone = phone;
  if (data.cboProduct && data.cboProduct.value)
    payload.productId = Number(data.cboProduct.value);
  if (data.cboDoctor && data.cboDoctor.value)
    payload.doctorId = Number(data.cboDoctor.value);
  if (data.cboInsurance && data.cboInsurance.value)
    payload.insuranceId = Number(data.cboInsurance.value);
  if (data.cboInstitution && data.cboInstitution.value)
    payload.institutionId = Number(data.cboInstitution.value);

  return payload;
};

const hasSearchCriteria = (payload) =>
  payload.document ||
  payload.name ||
  payload.lastName ||
  payload.phone ||
  payload.productId ||
  payload.doctorId ||
  payload.insuranceId ||
  payload.institutionId;

const buildResultColumns = (tableData, onGoToPatient) => [
  {
    name: "personId",
    label: "Id",
    options: { display: "excluded", filter: false, sort: false },
  },
  {
    name: "prescriptionId",
    label: "PrescriptionId",
    options: { display: "excluded", filter: false, sort: false },
  },
  defaultCol("code", "core.code"),
  defaultCol("document", "core.documentOnly"),
  defaultCol("fullName", "core.patient"),
  defaultCol("phone", "core.phone"),
  defaultCol("productName", "core.product"),
  defaultCol("doctorName", "core.doctor"),
  defaultCol("insuranceName", "core.insurance"),
  defaultCol("nurses", "patientSearch.nurse"),
  defaultCol("stateText", "core.status"),
  {
    name: "actions",
    label: IntlGetText("core.actions"),
    options: {
      filter: false,
      sort: false,
      customBodyRender: (value, tableMeta) => {
        const row = tableData[tableMeta.rowIndex];
        if (!row || !row.prescriptionId || !row.personId || !row.isAssignedToCurrentNurse) {
          return null;
        }
        return (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => onGoToPatient(row.prescriptionId, row.personId)}
          >
            <IntlMessages id="patientSearch.goToPatient" />
          </Button>
        );
      },
    },
  },
];

const mapResultsForTable = (items) =>
  (items || []).map((r) => ({
    personId: r.personId ?? r.PersonId,
    prescriptionId: r.prescriptionId ?? r.PrescriptionId,
    code: r.code || r.Code || "",
    document: r.document || r.Document || "",
    fullName: r.fullName || r.FullName || "",
    phone: r.phone || r.Phone || "",
    productName: r.productName || r.ProductName || "",
    doctorName: r.doctorName || r.DoctorName || "",
    insuranceName: r.insuranceName || r.InsuranceName || "",
    nurses: r.nurses || r.Nurses || "",
    stateText: r.stateText || r.StateText || "",
    isAssignedToCurrentNurse:
      r.isAssignedToCurrentNurse ?? r.IsAssignedToCurrentNurse ?? false,
  }));

const DialogContainer = ({ children, open, maxWidth, handleClickOpen, handleRequestClose, dialogTitle }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
  <div>
    <IconButton
      variant="contained"
      onClick={handleClickOpen}
      className="icon-btn text-white"
    >
      <i className={`zmdi zmdi-account-add zmdi-hc-fw`} />
    </IconButton>
    <Dialog
      open={open}
      fullWidth
      fullScreen={isSmallScreen}
      maxWidth={maxWidth}
      scroll="paper"
      className="new-patient-dialog"
      disableBackdropClick={true}
      onClose={handleRequestClose}
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      {children}
    </Dialog>
  </div>
  );
};

const NewPatient = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("search");
  const [searchState, setSearchState] = useState(initialSearchState);

  const methods = useForm({ defaultValues });
  const { reset } = methods;

  const handleClickOpen = () => setOpen(true);

  const handleRequestClose = () => {
    reset(defaultValues);
    setSearchState(initialSearchState);
    setStep("search");
    setOpen(false);
  };

  return (
    <DialogContainer
      open={open}
      maxWidth={step === "search" ? "lg" : "sm"}
      handleClickOpen={handleClickOpen}
      handleRequestClose={handleRequestClose}
      dialogTitle={
        step === "search" ? (
          <IntlMessages id="alert.newPatient" />
        ) : (
          <IntlMessages id="patientSearch.createTitle" />
        )
      }
    >
      {step === "search" ? (
        <PatientSearchStep
          handleRequestClose={handleRequestClose}
          onContinue={() => setStep("create")}
          methods={{ ...methods }}
          searchState={searchState}
          setSearchState={setSearchState}
        />
      ) : (
        <PatientCreateStep
          handleRequestClose={handleRequestClose}
          onBack={() => setStep("search")}
          methods={{ ...methods }}
        />
      )}
    </DialogContainer>
  );
};

const PatientSearchStep = ({
  handleRequestClose,
  onContinue,
  methods,
  searchState,
  setSearchState,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const fnSetLoader = (data) => dispatch(setLoader(data));

  const { errors, setValue, watch, getValues } = methods;

  const [insurances, setInsurance] = useState([]);
  const [institutions, setInstitution] = useState([]);
  const [doctors, setDoctor] = useState([]);
  const [products, setProduct] = useState([]);

  const insuranceValue = watch("cboInsurance");
  const institutionValue = watch("cboInstitution");
  const doctorValue = watch("cboDoctor");
  const productValue = watch("cboProduct");

  useEffect(() => {
    callApi(getInsuranceList, setInsurance, fnSetLoader);
    callApi(getProductList, setProduct, fnSetLoader);
    callApi(getInstitutionList, setInstitution, fnSetLoader);
    callApi(getDoctorList, setDoctor, fnSetLoader);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cboHandleChange = (name) => (event) => {
    setValue(name, event);
  };

  const runSearch = useCallback(
    async (page, rowsPerPage) => {
      const formData = getValues();
      const payload = buildSearchPayload(formData, page, rowsPerPage);

      if (!hasSearchCriteria(payload)) {
        NotificationManager.warning(
          IntlGetText("patientSearch.atLeastOneCriteria")
        );
        return;
      }

      await callApiAsync(
        () => postSearchPatients(payload),
        (data) => {
          const items = data.items || data.Items || [];
          setSearchState({
            hasSearched: true,
            results: items,
            totalCount: data.totalCount ?? data.TotalCount ?? 0,
            page: data.page ?? data.Page ?? 0,
            rowsPerPage: data.rowsPerPage ?? data.RowsPerPage ?? rowsPerPage,
          });
        },
        fnSetLoader
      );
    },
    [getValues, setSearchState, fnSetLoader]
  );

  const handleSearchClick = () => {
    runSearch(0, searchState.rowsPerPage);
  };

  const handleGoToPatient = (prescriptionId, personId) => {
    handleRequestClose();
    if (personId) {
      history.push({
        pathname: "/app/patient/detail",
        prescriptionId,
      });
      return;
    }
    callApi(
      () => getPrescriptions(0),
      (data) => {
        const prescription = data.find((p) => p.id === prescriptionId);
        if (prescription) {
          dispatch(setSelectedPrescription(prescription));
        }
        history.push("/app/management");
      },
      fnSetLoader
    );
  };

  const theme = useTheme();
  const intl = useIntl();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isCompactHeight = useMediaQuery("(max-height: 800px)");

  const tableData = mapResultsForTable(searchState.results);
  const tableColumns = searchState.hasSearched
    ? buildResultColumns(tableData, handleGoToPatient)
    : [];

  const tableOptions = {
    filter: false,
    viewColumns: false,
    download: false,
    print: false,
    search: false,
    selectableRows: "none",
    serverSide: true,
    fixedHeader: true,
    tableBodyHeight: isSmallScreen ? "140px" : isCompactHeight ? "160px" : "200px",
    count: searchState.totalCount,
    page: searchState.page,
    rowsPerPage: searchState.rowsPerPage,
    rowsPerPageOptions: [10, 15],
    onTableChange: (action, tableState) => {
      if (action === "changePage" || action === "changeRowsPerPage") {
        runSearch(tableState.page, tableState.rowsPerPage);
      }
    },
    textLabels: getTextLabels(),
  };

  return (
    <>
      <DialogContent className="new-patient-dialog-content">
        <FormProvider {...methods}>
          <form noValidate autoComplete="off">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" color="primary">
                  <IntlMessages id="patientSearch.title" />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <FormInput
                  label={<IntlMessages id="patientSearch.document" />}
                  name="searchDocument"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  errorobj={errors}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <FormInput
                  label={<IntlMessages id="patientSearch.name" />}
                  name="searchName"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  errorobj={errors}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <FormInput
                  label={<IntlMessages id="patientSearch.lastName" />}
                  name="searchLastName"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  errorobj={errors}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={3}>
                <FormInput
                  label={<IntlMessages id="patientSearch.phone" />}
                  name="searchPhone"
                  type="tel"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  inputProps={{ inputMode: "tel", autoComplete: "tel" }}
                  errorobj={errors}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <FormSelectAutoComplete
                  label={<IntlMessages id="core.insurance" />}
                  options={insurances}
                  name="cboInsurance"
                  value={insuranceValue}
                  onChange={cboHandleChange("cboInsurance")}
                  errorobj={errors}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <FormSelectAutoComplete
                  label={<IntlMessages id="core.institution" />}
                  options={institutions}
                  name="cboInstitution"
                  value={institutionValue}
                  onChange={cboHandleChange("cboInstitution")}
                  errorobj={errors}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <FormSelectAutoComplete
                  label={<IntlMessages id="core.product" />}
                  options={products}
                  name="cboProduct"
                  value={productValue}
                  onChange={cboHandleChange("cboProduct")}
                  errorobj={errors}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <FormSelectAutoComplete
                  label={<IntlMessages id="core.doctor" />}
                  options={doctors}
                  name="cboDoctor"
                  value={doctorValue}
                  onChange={cboHandleChange("cboDoctor")}
                  errorobj={errors}
                />
              </Grid>
              <Grid item xs={12} className="text-right">
                <ButtonAsync
                  onClick={handleSearchClick}
                  color="primary"
                  variant="contained"
                >
                  <IntlMessages id="patientSearch.search" />
                </ButtonAsync>
              </Grid>

              {searchState.hasSearched && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" className="mb-1">
                    <IntlMessages id="patientSearch.results" />
                  </Typography>
                  <Typography
                    variant="body2"
                    color={searchState.totalCount === 0 ? "textSecondary" : "primary"}
                    className="mb-2"
                  >
                    {searchState.totalCount === 0 ? (
                      <IntlMessages id="patientSearch.noResults" />
                    ) : (
                      intl.formatMessage(
                        { id: "patientSearch.resultsCount" },
                        { count: searchState.totalCount }
                      )
                    )}
                  </Typography>
                  <Table
                    className="border-dialog-card"
                    data={tableData}
                    columns={tableColumns}
                    options={tableOptions}
                  />
                </Grid>
              )}
            </Grid>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions className="new-patient-dialog-actions">
        <Button onClick={handleRequestClose} color="secondary">
          <IntlMessages id="core.cancel" />
        </Button>
        <Button
          onClick={onContinue}
          color="primary"
          variant="contained"
          disabled={!searchState.hasSearched}
        >
          <IntlMessages id="patientSearch.continue" />
        </Button>
      </DialogActions>
    </>
  );
};

const PatientCreateStep = ({ handleRequestClose, onBack, methods }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const fnSetLoader = (data) => dispatch(setLoader(data));

  const { handleSubmit, errors, setValue, watch } = methods;

  const [insurances, setInsurance] = useState([]);
  const [institutions, setInstitution] = useState([]);
  const [doctors, setDoctor] = useState([]);
  const [products, setProduct] = useState([]);

  const insuranceValue = watch("cboInsurance");
  const institutionValue = watch("cboInstitution");
  const doctorValue = watch("cboDoctor");
  const productValue = watch("cboProduct");

  useEffect(() => {
    callApi(getInsuranceList, setInsurance, fnSetLoader);
    callApi(getProductList, setProduct, fnSetLoader);
    callApi(getInstitutionList, setInstitution, fnSetLoader);
    callApi(getDoctorList, setDoctor, fnSetLoader);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cboHandleChange = (name) => (event) => {
    setValue(name, event);
  };

  const callGetPrescriptions = () => {
    callApi(
      () => getPrescriptions(0),
      (data) => {
        dispatch(loadPrescriptions(data, 0));
        history.push("/app/management");
      },
      fnSetLoader
    );
  };

  const prescriptionSaveSuccess = (data) => {
    handleRequestClose();
    if (
      getUserInfo().shardKey === ShardKeyEnum.ialuril ||
      getUserInfo().shardKey === ShardKeyEnum.seranestcr
    ) {
      history.push({
        pathname: "/app/patientPA",
        state: { reloadPage: new Date() },
      });
      return;
    }

    dispatch(setSelectedPrescription(data));
    callGetPrescriptions();
  };

  const onSubmit = async (data) => {
    if (
      !data.cboInsurance?.value ||
      !data.cboInstitution?.value ||
      !data.cboDoctor?.value ||
      !data.cboProduct?.value
    ) {
      NotificationManager.warning(
        IntlGetText("patientSearch.createFieldsRequired")
      );
      return;
    }

    const obj = {
      insuranceId: Number(data.cboInsurance.value),
      institutionId: Number(data.cboInstitution.value),
      doctorId: Number(data.cboDoctor.value),
      specialtyId: data.cboDoctor.specialtyId
        ? Number(data.cboDoctor.specialtyId)
        : null,
      productId: Number(data.cboProduct.value),
      comment: "",
    };

    await callApiAsync(
      () => postNewPatientAlert(obj),
      prescriptionSaveSuccess,
      fnSetLoader
    );
  };

  return (
    <>
      <DialogContent className="new-patient-dialog-content">
        <FormProvider {...methods}>
          <form noValidate autoComplete="off">
            <Grid container spacing={2}>
              <Grid item xs={12} md={12} lg={6}>
                <FormSelectAutoComplete
                  label={<IntlMessages id="core.insurance" />}
                  options={insurances}
                  required={true}
                  name="cboInsurance"
                  value={insuranceValue}
                  onChange={cboHandleChange("cboInsurance")}
                  errorobj={errors}
                />
              </Grid>
              <Grid item xs={12} md={12} lg={6}>
                <FormSelectAutoComplete
                  label={<IntlMessages id="core.institution" />}
                  options={institutions}
                  required={true}
                  name="cboInstitution"
                  value={institutionValue}
                  onChange={cboHandleChange("cboInstitution")}
                  errorobj={errors}
                />
              </Grid>
              <Grid item xs={12} md={12} lg={6}>
                <FormSelectAutoComplete
                  label={<IntlMessages id="core.product" />}
                  options={products}
                  required={true}
                  name="cboProduct"
                  value={productValue}
                  onChange={cboHandleChange("cboProduct")}
                  errorobj={errors}
                />
              </Grid>
              <Grid item xs={12} md={12} lg={6}>
                <FormSelectAutoComplete
                  label={<IntlMessages id="core.doctor" />}
                  options={doctors}
                  required={true}
                  name="cboDoctor"
                  value={doctorValue}
                  onChange={cboHandleChange("cboDoctor")}
                  errorobj={errors}
                />
              </Grid>
              <Grid item xs={12} md={12} lg={6}>
                <TextField
                  label={<IntlMessages id="core.specialty" />}
                  value={
                    doctorValue && doctorValue.specialty
                      ? doctorValue.specialty
                      : ""
                  }
                  variant="outlined"
                  fullWidth
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  helperText={
                    doctorValue && !doctorValue.specialty
                      ? "Especialidad no encontrada"
                      : ""
                  }
                  margin="dense"
                />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions className="new-patient-dialog-actions">
        <Button onClick={onBack} color="secondary">
          <IntlMessages id="patientSearch.back" />
        </Button>
        <Button onClick={handleRequestClose} color="secondary">
          <IntlMessages id="core.cancel" />
        </Button>
        <ButtonAsync onClick={handleSubmit(onSubmit)} color="primary" variant="contained">
          <IntlMessages id="core.save" />
        </ButtonAsync>
      </DialogActions>
    </>
  );
};

export default NewPatient;
